import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles, Menu, MenuItem } from '@material-ui/core'
import SydButton from '../../../../../../commonDesign/Button'
import { PRESENTATION_TYPE_OPTIONS } from '../../components/presentationManagement/defaultPresentationConfig'
import PurposeCard from './PurposeCard'
import PresentationOptionCard from './PresentationOptionCard'

const MONO = "'SF Mono', 'Fira Code', Menlo, Consolas, monospace"

const PRESENTATION_TYPES = PRESENTATION_TYPE_OPTIONS

const useStyles = makeStyles(() => ({
  root: {
    height: '100%',
    overflow: 'auto',
    padding: '16px 18px',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px'
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    paddingBottom: '4px',
    borderBottom: '1px solid var(--color-neutral-200)'
  },
  sectionTitle: {
    fontSize: '11px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    color: 'var(--color-neutral-700)',
    fontFamily: MONO
  },
  sectionCount: {
    fontSize: '11px',
    color: 'var(--color-neutral-400)',
    fontFamily: MONO
  },
  sectionDescription: {
    fontSize: '11px',
    color: 'var(--color-neutral-500)',
    fontStyle: 'italic'
  },
  sectionGrow: {
    flex: 1
  },
  cardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  empty: {
    border: '1px dashed var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-lg)',
    padding: '20px 16px',
    textAlign: 'center',
    color: 'var(--color-neutral-500)',
    fontSize: '12.5px',
    backgroundColor: 'var(--color-neutral-050)'
  },
  emptyHint: {
    marginTop: '4px',
    fontSize: '11px',
    color: 'var(--color-neutral-400)',
    fontStyle: 'italic'
  },
  emptyState: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '64px 32px',
    textAlign: 'center',
    color: 'var(--color-neutral-500)',
    gap: '12px'
  },
  emptyHeadline: {
    fontSize: '15px',
    fontWeight: 600,
    color: 'var(--color-neutral-700)',
    marginBottom: '6px'
  },
  emptyBody: {
    fontSize: '12.5px',
    lineHeight: 1.5,
    maxWidth: '360px',
    color: 'var(--color-neutral-500)'
  },
  emptyActions: {
    display: 'flex',
    gap: '8px',
    marginTop: '8px'
  }
}))

function DraftBrowser ({
  draft,
  onUpdatePurpose,
  onDeletePurpose,
  onAddPurpose,
  onUpdateOptionConfig,
  onUpdateOptionField,
  onDeleteOption,
  onAddOption,
  onPreviewOption,
  onAnalyzeDql
}) {
  const classes = useStyles()
  const [optionMenuAnchor, setOptionMenuAnchor] = useState(null)
  const [optionMenuAnchorEmpty, setOptionMenuAnchorEmpty] = useState(null)

  const purposes = draft?.data?.purposes || {}
  const options = draft?.presentation?.options || {}
  const purposeEntries = Object.entries(purposes)
  const optionEntries = Object.entries(options)
  const isEmpty = purposeEntries.length === 0 && optionEntries.length === 0

  const editable = Boolean(onUpdatePurpose && onUpdateOptionConfig)

  const handlePickOptionType = (type, closeMenu) => {
    closeMenu()
    if (onAddOption) onAddOption({ type })
  }

  if (isEmpty) {
    return (
      <div className={classes.root}>
        <div className={classes.emptyState}>
          <div className={classes.emptyHeadline}>Empty blueprint</div>
          <div className={classes.emptyBody}>
            This blueprint has no purposes or presentation options yet. Add one
            below, or ask the Designer chat to draft something — for example:
            <em> &ldquo;Build me an account summary grid scoped by client.&rdquo;</em>
          </div>
          {editable && (
            <div className={classes.emptyActions}>
              {onAddPurpose && (
                <SydButton
                  size='sm'
                  variant='secondary'
                  icon='plus'
                  onClick={() => onAddPurpose()}
                >
                  Add purpose
                </SydButton>
              )}
              {onAddOption && (
                <>
                  <SydButton
                    size='sm'
                    variant='secondary'
                    icon='plus'
                    onClick={(e) => setOptionMenuAnchorEmpty(e.currentTarget)}
                  >
                    Add presentation option
                  </SydButton>
                  <Menu
                    anchorEl={optionMenuAnchorEmpty}
                    open={Boolean(optionMenuAnchorEmpty)}
                    onClose={() => setOptionMenuAnchorEmpty(null)}
                  >
                    {PRESENTATION_TYPES.map(t => (
                      <MenuItem
                        key={t.value}
                        onClick={() => handlePickOptionType(t.value, () => setOptionMenuAnchorEmpty(null))}
                      >
                        {t.label}
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={classes.root}>
      <div className={classes.section}>
        <div className={classes.sectionHeader}>
          <span className={classes.sectionTitle}>Purposes</span>
          <span className={classes.sectionCount}>
            {purposeEntries.length} item{purposeEntries.length === 1 ? '' : 's'}
          </span>
          <span className={classes.sectionDescription}>
            Each purpose is a query (DQL) that produces rows
          </span>
          <span className={classes.sectionGrow} />
          {onAddPurpose && (
            <SydButton
              size='sm'
              variant='ghost'
              icon='plus'
              onClick={() => onAddPurpose()}
            >
              Add purpose
            </SydButton>
          )}
        </div>
        {purposeEntries.length === 0 ? (
          <div className={classes.empty}>
            No purposes yet
            <div className={classes.emptyHint}>
              Click &ldquo;Add purpose&rdquo; or ask the Designer to draft one from a description or DQL snippet.
            </div>
          </div>
        ) : (
          <div className={classes.cardList}>
            {purposeEntries.map(([code, purpose]) => (
              <PurposeCard
                key={code}
                purposeCode={code}
                purpose={purpose}
                defaultExpanded={purposeEntries.length === 1}
                onUpdatePurpose={onUpdatePurpose}
                onDeletePurpose={onDeletePurpose}
                onAnalyzeDql={onAnalyzeDql}
              />
            ))}
          </div>
        )}
      </div>

      <div className={classes.section}>
        <div className={classes.sectionHeader}>
          <span className={classes.sectionTitle}>Presentation options</span>
          <span className={classes.sectionCount}>
            {optionEntries.length} item{optionEntries.length === 1 ? '' : 's'}
          </span>
          <span className={classes.sectionDescription}>
            How rows from a purpose are displayed
          </span>
          <span className={classes.sectionGrow} />
          {onAddOption && (
            <>
              <SydButton
                size='sm'
                variant='ghost'
                icon='plus'
                onClick={(e) => setOptionMenuAnchor(e.currentTarget)}
              >
                Add option
              </SydButton>
              <Menu
                anchorEl={optionMenuAnchor}
                open={Boolean(optionMenuAnchor)}
                onClose={() => setOptionMenuAnchor(null)}
              >
                {PRESENTATION_TYPES.map(t => (
                  <MenuItem
                    key={t.value}
                    onClick={() => handlePickOptionType(t.value, () => setOptionMenuAnchor(null))}
                  >
                    {t.label}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </div>
        {optionEntries.length === 0 ? (
          <div className={classes.empty}>
            No presentation options yet
            <div className={classes.emptyHint}>
              Once you have a purpose, click &ldquo;Add option&rdquo; or ask the Designer for a grid, metric, chart, or hierarchy.
            </div>
          </div>
        ) : (
          <div className={classes.cardList}>
            {optionEntries.map(([code, option]) => (
              <PresentationOptionCard
                key={code}
                optionCode={code}
                option={option}
                allPurposes={purposes}
                defaultExpanded={optionEntries.length === 1}
                onUpdateConfig={onUpdateOptionConfig}
                onUpdateOptionField={onUpdateOptionField}
                onDeleteOption={onDeleteOption}
                onPreview={onPreviewOption}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

DraftBrowser.propTypes = {
  draft: PropTypes.object,
  onUpdatePurpose: PropTypes.func,
  onDeletePurpose: PropTypes.func,
  onAddPurpose: PropTypes.func,
  onUpdateOptionConfig: PropTypes.func,
  onUpdateOptionField: PropTypes.func,
  onDeleteOption: PropTypes.func,
  onAddOption: PropTypes.func,
  onPreviewOption: PropTypes.func,
  onAnalyzeDql: PropTypes.func
}

export default DraftBrowser
